import { useId, type FormEvent } from 'react'
import { Icon } from '@iconify/react'
import { CpfCnpjInput } from '@/components/CpfCnpjInput'
import type { RaspadinhaPublicCampaign } from '@/types/raspadinha-public-campaign'
import { maskBrazilianPhone } from '@/lib/person-data'
import { useRaspadinhaRegisterFlow } from './useRaspadinhaRegisterFlow'
import styles from './RaspadinhaPublicRegisterStep.module.css'

type RaspadinhaPublicRegisterStepProps = {
  campaign: RaspadinhaPublicCampaign
  onRegistered: () => void
}

type RegisterIdentityFieldsProps = {
  formId: string
  registerForm: {
    name: string
    email: string
    phone: string
    document: string
  }
  fieldErrors: Partial<Record<'name' | 'email' | 'phone' | 'document', string>>
  isSubmitting: boolean
  onUpdateField: (field: 'name' | 'email' | 'phone' | 'document', value: string) => void
  showPhone?: boolean
}

function RegisterIdentityFields({
  formId,
  registerForm,
  fieldErrors,
  isSubmitting,
  onUpdateField,
  showPhone = true,
}: RegisterIdentityFieldsProps) {
  return (
    <>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${formId}-name`}>
          Nome completo
        </label>
        <input
          id={`${formId}-name`}
          className={[styles.input, fieldErrors.name ? styles.inputInvalid : '']
            .filter(Boolean)
            .join(' ')}
          value={registerForm.name}
          onChange={(event) => onUpdateField('name', event.target.value)}
          autoComplete="name"
          autoCapitalize="words"
          enterKeyHint="next"
          aria-invalid={Boolean(fieldErrors.name)}
          disabled={isSubmitting}
        />
        {fieldErrors.name ? (
          <p className={styles.fieldError} role="alert">
            {fieldErrors.name}
          </p>
        ) : null}
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${formId}-email`}>
          E-mail
        </label>
        <input
          id={`${formId}-email`}
          type="email"
          inputMode="email"
          className={[styles.input, fieldErrors.email ? styles.inputInvalid : '']
            .filter(Boolean)
            .join(' ')}
          value={registerForm.email}
          onChange={(event) => onUpdateField('email', event.target.value)}
          autoComplete="email"
          enterKeyHint="next"
          aria-invalid={Boolean(fieldErrors.email)}
          disabled={isSubmitting}
        />
        {fieldErrors.email ? (
          <p className={styles.fieldError} role="alert">
            {fieldErrors.email}
          </p>
        ) : null}
      </div>

      {showPhone ? (
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-phone`}>
            WhatsApp
          </label>
          <input
            id={`${formId}-phone`}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            className={[styles.input, fieldErrors.phone ? styles.inputInvalid : '']
              .filter(Boolean)
              .join(' ')}
            value={registerForm.phone}
            onChange={(event) =>
              onUpdateField('phone', maskBrazilianPhone(event.target.value))
            }
            autoComplete="tel"
            enterKeyHint="next"
            placeholder="(00) 00000-0000"
            aria-invalid={Boolean(fieldErrors.phone)}
            disabled={isSubmitting}
          />
          {fieldErrors.phone ? (
            <p className={styles.fieldError} role="alert">
              {fieldErrors.phone}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${formId}-document`}>
          CPF
        </label>
        <CpfCnpjInput
          id={`${formId}-document`}
          mode="cpf"
          className={[styles.input, fieldErrors.document ? styles.inputInvalid : '']
            .filter(Boolean)
            .join(' ')}
          value={registerForm.document}
          onChange={(maskedValue) => onUpdateField('document', maskedValue)}
          enterKeyHint="done"
          disabled={isSubmitting}
          aria-invalid={Boolean(fieldErrors.document)}
        />
        {fieldErrors.document ? (
          <p className={styles.fieldError} role="alert">
            {fieldErrors.document}
          </p>
        ) : null}
      </div>
    </>
  )
}

export function RaspadinhaPublicRegisterStep({
  campaign,
  onRegistered,
}: RaspadinhaPublicRegisterStepProps) {
  return (
    <RaspadinhaRegisterWizard
      campaign={campaign}
      institutionId={campaign.institution_id}
      onRegistered={onRegistered}
    />
  )
}

function RaspadinhaRegisterWizard({
  campaign,
  institutionId,
  onRegistered,
}: RaspadinhaPublicRegisterStepProps & { institutionId: number | null }) {
  const formId = useId()
  const {
    flowStep,
    flowContent,
    phone,
    setPhone,
    loginPassword,
    setLoginPassword,

    registerForm,
    updateRegisterField,
    fieldErrors,
    submitError,
    isSubmitting,
    handlePrimaryAction,
    editPhone,
    startRecovery,
  } = useRaspadinhaRegisterFlow({
    slug: campaign.slug,
    institutionId,
    onRegistered,
  })

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void handlePrimaryAction()
  }

  return (
    <section className={styles.step} aria-labelledby={`${formId}-title`}>
      <h1 id={`${formId}-title`} className={styles.title}>
        {flowContent.title}
      </h1>
      <p className={styles.subtitle}>{flowContent.description}</p>

      {flowContent.notice ? <p className={styles.notice}>{flowContent.notice}</p> : null}

      {flowStep !== 'phone' ? (
        <div className={styles.phoneSummary}>
          <span className={styles.phoneSummaryLabel}>WhatsApp</span>
          <strong>{phone}</strong>
          <button
            type="button"
            className={styles.linkButton}
            onClick={editPhone}
            disabled={isSubmitting}
          >
            Alterar
          </button>
        </div>
      ) : null}

      <form className={styles.form} onSubmit={handleFormSubmit} noValidate>
        {flowStep === 'phone' ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor={`${formId}-phone`}>
              WhatsApp
            </label>
            <input
              id={`${formId}-phone`}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              className={styles.input}
              value={phone}
              onChange={(event) => setPhone(maskBrazilianPhone(event.target.value))}
              autoComplete="tel"
              enterKeyHint="done"
              placeholder="(00) 00000-0000"
              disabled={isSubmitting}
            />
          </div>
        ) : null}

        {flowStep === 'password' ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor={`${formId}-password`}>
              Senha
            </label>
            <input
              id={`${formId}-password`}
              type="password"
              className={styles.input}
              value={loginPassword}
              onChange={(event) => setLoginPassword(event.target.value)}
              autoComplete="current-password"
              enterKeyHint="done"
              disabled={isSubmitting}
            />
          </div>
        ) : null}

        {flowStep === 'password' && (
          <button
            type="button"
            className={styles.linkButton}
            onClick={startRecovery}
            disabled={isSubmitting}
          >
            Esqueci minha senha
          </button>
        )}

        {flowStep === 'register' ? (
          <RegisterIdentityFields
            formId={formId}
            registerForm={registerForm}
            fieldErrors={fieldErrors}
            isSubmitting={isSubmitting}
            onUpdateField={updateRegisterField}
            showPhone={false}
          />
        ) : null}

        {submitError ? (
          <p className={styles.errorMessage} role="alert">
            {submitError}
          </p>
        ) : null}

        <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Icon icon="mdi:loading" className={styles.loadingIcon} aria-hidden />
              {flowContent.loadingLabel}
            </>
          ) : (
            flowContent.buttonLabel
          )}
        </button>
      </form>
    </section>
  )
}
